import { Button, Tooltip } from '@myua/kit';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Asset, getAssetLicenses, urlDownloadBulk } from '../../../api';
import { LicenseStatus } from '../../../../general/model';
import { getPublicDownloadFilterFn } from '../../sharing/selectors';
import { getAssetLabLogger } from '../../app/logs';
import { DocumentDownload } from '@myua/icons/dist/bulk16';
import { downloadDOM } from '../../../../general/utils/download';
import { getPage } from '../../router/selectors';
import { getSharingId } from '../../sharing/selectors';
import { Page } from '../../router/model';

export const DownloadButton: React.FC<{ assets: Asset[] }> = ({ assets }) => {
  const publicDownloadFilterFn = useSelector(getPublicDownloadFilterFn);
  const assetLabLogger = useSelector(getAssetLabLogger);
  const page = useSelector(getPage);
  const sharingId = useSelector(getSharingId);

  const items = useMemo(() => publicDownloadFilterFn(assets), [assets, publicDownloadFilterFn]);

  // Блокировка скачивания по решению бэкенда (MYUA-16456): если среди файлов есть хоть один
  // с downloading_allowed === false (напр. шрифт под модерацией), кнопка задизейблена с
  // предупреждением. Для одиночного файла уточняем причину по статусу лицензии, если он известен.
  const blockedTooltip = useMemo(() => {
    const blocked = assets.filter((asset) => asset.downloading_allowed === false);
    if (blocked.length === 0) return undefined;
    if (blocked.length > 1) return 'Some files are unavailable for download';
    // У архива со шрифтами лицензий несколько, и скачивание закрыто, пока хотя бы одна
    // не одобрена. Отказ юриста — более определённая причина, чем «ещё на модерации»,
    // поэтому при смеси статусов показываем именно его.
    const statuses = getAssetLicenses(blocked[0]).map((license) => license.status);
    if (statuses.includes(LicenseStatus.Rejected)) return 'Download rejected by legal';
    if (statuses.includes(LicenseStatus.UnderModeration)) return 'File is under legal moderation';
    return 'This file is unavailable for download';
  }, [assets]);

  const downloadBlocked = blockedTooltip !== undefined;

  return (
    // antd не триггерит hover-тултип на нативном disabled-элементе, поэтому Button
    // оборачиваем в span-обёртку внутри Tooltip (паттерн из UploadButton.tsx).
    <Tooltip title={blockedTooltip}>
      <span className="al-download-button-wrap">
        <Button
          disabled={items.length === 0 || downloadBlocked}
          type="primary"
          onClick={() => {
            let url;
            if (items.length === 1) {
              url = items[0].download_url ?? '';
            } else {
              url = urlDownloadBulk(
                items.map(({ id }) => id),
                page === Page.sharing ? sharingId : undefined,
              );
            }
            downloadDOM(url);
            assetLabLogger({
              action: 'Assets',
              sub_action: 'Download',
              extra: {
                // asset_ids: items.map(({ id }) => id),
                text_1: 'button',
              },
            });
          }}
          icon={<DocumentDownload />}
        >
          Download
        </Button>
      </span>
    </Tooltip>
  );
};
